import React, { useCallback, useEffect, useState } from 'react';
import { useLocale } from '../i18n/LocaleContext';
import { fetchPayouts } from '../api/creators';
import './CreatorTabs.scss';

// Payouts tab — mirrors the partner portal's Payouts look (gradient balance
// hero + history), driven by GET /creators/payouts. Payouts are made
// automatically (monthly, by the 10th), so there is no request action.
export default function PayoutsTab() {
  const { t } = useLocale();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setData(await fetchPayouts());
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="panel-card">
        <div className="panel-card__muted">{t('shell.loading')}</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="panel-card">
        <div className="panel-card__muted">{t('payouts.loadError')}</div>
        <button className="btn-o btn-o--dark btn-o--sm" onClick={load}>
          {t('shell.retry')}
        </button>
      </div>
    );
  }

  const currency = (data.currency || '').toUpperCase();
  const cur = (n) =>
    n == null ? '—' : `${Number(n).toLocaleString()} ${currency}`.trim();

  const available = data.available;

  // The endpoint already sends only what is still owed. Kept as a belt-and-
  // braces filter so a cached bundle talking to a fresh API can't list a paid
  // earning as outstanding — and it drops anything cancelled either way.
  const pendingEarnings = (data.earnings || []).filter(
    (e) => e.status === 'pending'
  );

  return (
    <div className="panel-card">
      <h2 className="panel-card__title">{t('payouts.title')}</h2>

      {/* Available balance hero */}
      <div className="balance-hero">
        <div className="balance-hero__main">
          <div className="balance-hero__label">{t('payouts.available')}</div>
          <div className="balance-hero__value">{cur(available)}</div>
          <div className="balance-hero__note">{t('payouts.note')}</div>
        </div>
      </div>

      {/* Earnings by video */}
      <div className="stat-block">
        <div className="stat-block__head">
          <span>{t('payouts.earningsTitle')}</span>
        </div>
        {pendingEarnings.length ? (
          <div className="payout-list">
            {pendingEarnings.map((e) => (
              <div className="payout-row" key={e.id}>
                <span className="payout-row__amount">{cur(e.amount)}</span>
                <span className="payout-row__brief">{e.brief}</span>
                <span className="payout-row__date">{e.date}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="panel-card__muted">{t('payouts.noEarnings')}</div>
        )}
      </div>

      {/* Payout history */}
      <div className="stat-block">
        <div className="stat-block__head">
          <span>{t('payouts.history')}</span>
        </div>
        {data.history?.length ? (
          <div className="payout-list">
            {data.history.map((p) => (
              <div className="payout-row" key={p.id}>
                <span className="payout-row__amount">{cur(p.amount)}</span>
                {/* Each row is one settled brief fee now, so it says which
                    brief. No status badge: everything in this list is paid. */}
                <span className="payout-row__brief">{p.brief}</span>
                <span className="payout-row__date">{p.date}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="panel-card__muted">{t('payouts.noPayouts')}</div>
        )}
      </div>
    </div>
  );
}
